import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  ExternalLink, MapPin, Phone, Clock, ChevronDown, ChevronUp,
  Utensils, Heart, Home, DollarSign, Users, FileText, AlertCircle
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

export default function ResourcesPage() {
  const { t } = useTranslation();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [selectedCounty, setSelectedCounty] = useState('all');

  const counties = [
    { id: 'all', name: 'All Counties' },
    { id: 'osceola', name: 'Osceola County' },
    { id: 'orange', name: 'Orange County' },
    { id: 'polk', name: 'Polk County' },
    { id: 'brevard', name: 'Brevard County' }
  ];

  const resourceCategories = [
    {
      id: 'food',
      title: 'Food Assistance & SNAP',
      icon: Utensils,
      color: 'bg-olive',
      description: 'Food stamps, SNAP benefits, and food assistance programs',
      resources: [
        {
          name: 'Florida ACCESS - SNAP/Food Stamps Application',
          description: 'Apply for SNAP (food stamps), cash assistance, and Medicaid online',
          url: 'https://www.myflorida.com/accessflorida/',
          phone: '1-866-762-2237',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Osceola Council on Aging - Senior Food Programs',
          description: 'Meals on Wheels and congregate meal programs for seniors 60+',
          url: 'https://www.osceolagenerations.org/',
          phone: '(407) 846-8532',
          counties: ['osceola'],
          type: 'Local Service'
        },
        {
          name: 'Second Harvest Food Bank of Central Florida',
          description: 'Find local food pantries and mobile food distributions',
          url: 'https://www.feedhopenow.org/find-food/',
          phone: '(407) 295-1066',
          counties: ['osceola', 'orange'],
          type: 'Food Bank'
        },
        {
          name: 'Feeding Tampa Bay - Polk County',
          description: 'Food pantry locator for Polk County area',
          url: 'https://feedingtampabay.org/find-food/',
          phone: '(813) 254-1190',
          counties: ['polk'],
          type: 'Food Bank'
        },
        {
          name: 'Brevard County Food Pantries',
          description: 'Daily Bread Inc. and local food assistance',
          url: 'https://dailybreadinc.org/',
          phone: '(321) 636-4208',
          counties: ['brevard'],
          type: 'Food Bank'
        },
        {
          name: 'WIC - Women, Infants & Children',
          description: 'Nutrition program for pregnant women, new mothers, and children under 5',
          url: 'https://www.floridahealth.gov/programs-and-services/wic/',
          phone: '1-800-342-3556',
          counties: ['all'],
          type: 'Application'
        }
      ]
    },
    {
      id: 'medical',
      title: 'Medical & Health Assistance',
      icon: Heart,
      color: 'bg-burgundy',
      description: 'Medicaid, Medicare, and healthcare assistance for seniors and low-income families',
      resources: [
        {
          name: 'Florida Medicaid Application',
          description: 'Apply for Medicaid health coverage through ACCESS Florida',
          url: 'https://www.myflorida.com/accessflorida/',
          phone: '1-866-762-2237',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Medicare.gov - Official Medicare Site',
          description: 'Medicare enrollment, plan finder, and coverage information',
          url: 'https://www.medicare.gov/',
          phone: '1-800-633-4227',
          counties: ['all'],
          type: 'Federal Program'
        },
        {
          name: 'Osceola County Health Department',
          description: 'Low-cost health services, immunizations, and WIC',
          url: 'https://osceola.floridahealth.gov/',
          phone: '(407) 343-2000',
          counties: ['osceola'],
          type: 'Local Service'
        },
        {
          name: 'Orange County Health Department',
          description: 'Community health services and disease prevention',
          url: 'https://orange.floridahealth.gov/',
          phone: '(407) 858-1400',
          counties: ['orange'],
          type: 'Local Service'
        },
        {
          name: 'Community Health Centers of Polk County',
          description: 'Affordable healthcare for uninsured and underinsured',
          url: 'https://www.chcpinc.org/',
          phone: '(863) 413-8600',
          counties: ['polk'],
          type: 'Local Service'
        },
        {
          name: 'Brevard Health Alliance',
          description: 'Primary care services on a sliding fee scale',
          url: 'https://brevardhealth.org/',
          phone: '(321) 241-6800',
          counties: ['brevard'],
          type: 'Local Service'
        },
        {
          name: 'SHINE - Free Medicare Counseling',
          description: 'Free unbiased Medicare counseling for seniors',
          url: 'https://www.floridashine.org/',
          phone: '1-800-963-5337',
          counties: ['all'],
          type: 'Free Service'
        }
      ]
    },
    {
      id: 'housing',
      title: 'Rent & Utility Assistance',
      icon: Home,
      color: 'bg-purple',
      description: 'Help with rent, mortgage, and utility bills',
      resources: [
        {
          name: 'Our Florida - Emergency Rental Assistance',
          description: 'State program for rent and utility assistance',
          url: 'https://www.ourflorida.com/',
          phone: '1-833-493-0594',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Osceola County Housing Assistance',
          description: 'Local emergency rental and utility assistance',
          url: 'https://www.osceola.org/agencies-departments/human-services/',
          phone: '(407) 742-8400',
          counties: ['osceola'],
          type: 'Local Service'
        },
        {
          name: 'Orange County CARES Housing Assistance',
          description: 'Emergency rental assistance for Orange County residents',
          url: 'https://www.orangecountyfl.net/FamiliesHealthSocialSvcs/',
          phone: '(407) 836-7600',
          counties: ['orange'],
          type: 'Local Service'
        },
        {
          name: 'Polk County Housing Authority',
          description: 'Section 8 housing vouchers and public housing',
          url: 'https://polkha.org/',
          phone: '(863) 688-7751',
          counties: ['polk'],
          type: 'Local Service'
        },
        {
          name: 'Brevard County Housing & Human Services',
          description: 'Emergency housing assistance and homeless services',
          url: 'https://www.brevardfl.gov/HousingAndHumanServices',
          phone: '(321) 633-2076',
          counties: ['brevard'],
          type: 'Local Service'
        },
        {
          name: 'LIHEAP - Utility Bill Assistance',
          description: 'Low Income Home Energy Assistance Program',
          url: 'https://www.benefits.gov/benefit/623',
          phone: '1-866-352-4681',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Duke Energy Assistance Programs',
          description: 'Payment plans and assistance for Duke Energy customers',
          url: 'https://www.duke-energy.com/home/billing/assistance-programs',
          phone: '1-800-700-8744',
          counties: ['all'],
          type: 'Utility Program'
        },
        {
          name: 'OUC Project CARE',
          description: 'Utility assistance for OUC customers in need',
          url: 'https://www.ouc.com/residential/customer-support/payment-options-and-assistance',
          phone: '(407) 423-9018',
          counties: ['orange', 'osceola'],
          type: 'Utility Program'
        }
      ]
    },
    {
      id: 'financial',
      title: 'Financial & Cash Assistance',
      icon: DollarSign,
      color: 'bg-gold',
      description: 'Cash assistance, SSI, and financial support programs',
      resources: [
        {
          name: 'Temporary Cash Assistance (TCA)',
          description: 'Monthly cash assistance for families with children',
          url: 'https://www.myflorida.com/accessflorida/',
          phone: '1-866-762-2237',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Social Security Administration',
          description: 'SSI, SSDI, and retirement benefits',
          url: 'https://www.ssa.gov/',
          phone: '1-800-772-1213',
          counties: ['all'],
          type: 'Federal Program'
        },
        {
          name: 'Supplemental Security Income (SSI)',
          description: 'Monthly payments for disabled, blind, or 65+ with limited income',
          url: 'https://www.ssa.gov/ssi/',
          phone: '1-800-772-1213',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Volunteer Income Tax Assistance (VITA)',
          description: 'Free tax preparation for low-income families',
          url: 'https://www.irs.gov/individuals/free-tax-return-preparation-for-qualifying-taxpayers',
          phone: '1-800-906-9887',
          counties: ['all'],
          type: 'Free Service'
        }
      ]
    },
    {
      id: 'seniors',
      title: 'Senior Services',
      icon: Users,
      color: 'bg-olive',
      description: 'Programs and assistance specifically for seniors 60+',
      resources: [
        {
          name: 'Elder Helpline - Florida',
          description: '24/7 helpline for seniors and caregivers',
          url: 'https://elderaffairs.org/',
          phone: '1-800-963-5337',
          counties: ['all'],
          type: 'Helpline'
        },
        {
          name: 'Osceola Council on Aging',
          description: 'Meals, transportation, and activities for Osceola seniors',
          url: 'https://www.osceolagenerations.org/',
          phone: '(407) 846-8532',
          counties: ['osceola'],
          type: 'Local Service'
        },
        {
          name: 'Orange County Office on Aging',
          description: 'Senior centers, meals, and support services',
          url: 'https://www.orangecountyfl.net/FamiliesHealthSocialSvcs/Seniors.aspx',
          phone: '(407) 836-7446',
          counties: ['orange'],
          type: 'Local Service'
        },
        {
          name: 'Polk County Senior Services',
          description: 'Congregate meals, home care, and transportation',
          url: 'https://www.polk-county.net/senior-services',
          phone: '(863) 534-5368',
          counties: ['polk'],
          type: 'Local Service'
        },
        {
          name: 'Brevard County Aging Services',
          description: 'Meals on Wheels and senior support programs',
          url: 'https://elderaffairs.org/',
          phone: '(321) 752-1264',
          counties: ['brevard'],
          type: 'Local Service'
        },
        {
          name: 'Florida Senior Legal Helpline',
          description: 'Free legal advice for seniors 60+',
          url: 'https://www.floridalawhelp.org/',
          phone: '1-888-895-7873',
          counties: ['all'],
          type: 'Free Service'
        }
      ]
    },
    {
      id: 'documents',
      title: 'Important Applications & Forms',
      icon: FileText,
      color: 'bg-charcoal',
      description: 'Direct links to apply for government assistance programs',
      resources: [
        {
          name: 'ACCESS Florida - All Benefits',
          description: 'One application for SNAP, Medicaid, and cash assistance',
          url: 'https://www.myflorida.com/accessflorida/',
          phone: '1-866-762-2237',
          counties: ['all'],
          type: 'Main Application'
        },
        {
          name: 'Apply for Medicare Online',
          description: 'Medicare enrollment for those 65+ or disabled',
          url: 'https://www.ssa.gov/medicare/',
          phone: '1-800-772-1213',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Social Security Benefits Application',
          description: 'Apply for retirement, disability, or survivor benefits',
          url: 'https://www.ssa.gov/benefits/retirement/',
          phone: '1-800-772-1213',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Affordable Care Act Marketplace',
          description: 'Health insurance for those not eligible for Medicaid',
          url: 'https://www.healthcare.gov/',
          phone: '1-800-318-2596',
          counties: ['all'],
          type: 'Application'
        },
        {
          name: 'Benefits.gov - Benefit Finder',
          description: 'Find all government benefits you may qualify for',
          url: 'https://www.benefits.gov/',
          phone: 'N/A',
          counties: ['all'],
          type: 'Tool'
        }
      ]
    }
  ];

  const filteredResources = resourceCategories.map(category => ({
    ...category,
    resources: category.resources.filter(
      r => selectedCounty === 'all' || r.counties.includes('all') || r.counties.includes(selectedCounty)
    )
  }));

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="resources-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 bg-gradient-to-b from-burgundy to-burgundy/90">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl text-white mb-6">
              Community Resources
            </h1>
            <p className="text-white/80 text-lg md:text-xl max-w-2xl mx-auto">
              Find help with food, housing, medical care, and more in Central Florida
            </p>
          </motion.div>
        </div>
      </section>

      {/* Living Fruit Ministry Banner */}
      <section className="bg-gold/20 border-b border-gold/30">
        <div className="max-w-6xl mx-auto px-6 py-6">
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex flex-col md:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3">
              <AlertCircle className="text-burgundy flex-shrink-0 mt-1" size={24} />
              <div>
                <p className="text-charcoal font-medium text-lg">
                  Living Fruit Ministry Food Pantry
                </p>
                <p className="text-charcoal/70">
                  Please check weekly for updates on where we will be distributing food each week!
                </p>
              </div>
            </div>
            <Link
              to="/pantries"
              className="flex-shrink-0 px-6 py-3 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors flex items-center gap-2"
              data-testid="pantry-link-btn"
            >
              <MapPin size={18} />
              View Our Pantry Locations
            </Link>
          </motion.div>
        </div>
      </section>

      {/* County Filter */}
      <section className="bg-white border-b border-gold/20">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-charcoal/60 text-sm font-medium">Filter by County:</span>
            {counties.map(county => (
              <button
                key={county.id}
                onClick={() => setSelectedCounty(county.id)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                  selectedCounty === county.id
                    ? 'bg-burgundy text-white'
                    : 'bg-cream text-charcoal hover:bg-gold/20'
                }`}
              >
                {county.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Resources Grid */}
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6">
          <div className="space-y-4">
            {filteredResources.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl border border-gold/10 overflow-hidden"
              >
                {/* Category Header */}
                <button
                  onClick={() => toggleCategory(category.id)}
                  className="w-full px-6 py-5 flex items-center justify-between hover:bg-cream/50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className={`${category.color} p-3 rounded-xl`}>
                      <category.icon size={24} className="text-white" />
                    </div>
                    <div className="text-left">
                      <h2 className="font-serif text-xl text-charcoal">{category.title}</h2>
                      <p className="text-charcoal/60 text-sm">{category.description}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-charcoal/50 bg-cream px-3 py-1 rounded-full">
                      {category.resources.length} resources
                    </span>
                    {expandedCategory === category.id ? (
                      <ChevronUp size={24} className="text-charcoal/50" />
                    ) : (
                      <ChevronDown size={24} className="text-charcoal/50" />
                    )}
                  </div>
                </button>

                {/* Resources List */}
                {expandedCategory === category.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="border-t border-gold/10"
                  >
                    <div className="divide-y divide-gold/10">
                      {category.resources.map((resource, i) => (
                        <div key={i} className="px-6 py-4 hover:bg-cream/30 transition-colors">
                          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <h3 className="font-medium text-charcoal">{resource.name}</h3>
                                <span className={`text-xs px-2 py-0.5 rounded-full ${
                                  resource.type === 'Application' ? 'bg-burgundy/10 text-burgundy' :
                                  resource.type === 'Local Service' ? 'bg-olive/10 text-olive' :
                                  resource.type === 'Food Bank' ? 'bg-gold/10 text-gold' :
                                  'bg-purple/10 text-purple'
                                }`}>
                                  {resource.type}
                                </span>
                              </div>
                              <p className="text-charcoal/60 text-sm">{resource.description}</p>
                              {resource.phone && resource.phone !== 'N/A' && (
                                <p className="text-charcoal/50 text-sm mt-1 flex items-center gap-1">
                                  <Phone size={12} /> {resource.phone}
                                </p>
                              )}
                            </div>
                            <a
                              href={resource.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center gap-2 px-4 py-2 bg-burgundy/10 text-burgundy rounded-lg text-sm font-medium hover:bg-burgundy hover:text-white transition-colors flex-shrink-0"
                            >
                              <ExternalLink size={14} /> Visit Website
                            </a>
                          </div>
                        </div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Need Help Section */}
      <section className="py-12 bg-olive/10">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-charcoal mb-4">
            Need Help Applying?
          </h2>
          <p className="text-charcoal/70 mb-6">
            Our ministry team is here to help you navigate these resources. Contact us and we'll guide you through the process.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-6 py-3 bg-burgundy text-white rounded-full font-medium hover:bg-purple transition-colors"
          >
            Contact Us for Assistance
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-charcoal/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-charcoal/50 text-sm">
            <strong>Disclaimer:</strong> Living Fruit Ministry provides these links as a community service. 
            We are not affiliated with these government programs or organizations. 
            Please contact each agency directly for the most current information and eligibility requirements.
          </p>
        </div>
      </section>
    </div>
  );
}
